import { Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useKdsRealtime } from "@/hooks/useKdsRealtime";
import { Loader2, ShieldX, Store } from "lucide-react";

export default function KdsLayout() {
  const { user, loading: authLoading } = useAuth();
  const { restaurants, activeRestaurant, setActiveRestaurant, isLoading: restaurantLoading } = useRestaurantContext();
  const { canAccessKDS, isLoading: roleLoading } = useUserRole();
  const navigate = useNavigate();

  // Start realtime processing for KDS items
  useKdsRealtime();

  if (authLoading || restaurantLoading || roleLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-zinc-900 text-white">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Access control: block unauthorized roles
  if (!canAccessKDS) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-zinc-900 text-white gap-6">
        <ShieldX className="h-16 w-16 text-red-400" />
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Acesso restrito</h1>
          <p className="text-zinc-400">Você não tem permissão para acessar o KDS.</p>
          <p className="text-zinc-500 text-sm">Solicite ao gerente ou proprietário.</p>
        </div>
        <button
          onClick={() => navigate("/")}
          className="px-6 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-white transition-colors"
        >
          Voltar
        </button>
      </div>
    );
  }

  // Multi-restaurant selector: show if user has multiple restaurants and none actively selected for KDS
  if (restaurants.length > 1 && !activeRestaurant) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-zinc-900 text-white p-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold mb-2">KDS - Selecione o Restaurante</h1>
          <p className="text-zinc-400 text-lg">Escolha o restaurante para acessar o KDS</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl w-full">
          {restaurants.map((restaurant) => (
            <button
              key={restaurant.id}
              onClick={() => setActiveRestaurant(restaurant)}
              className="group flex flex-col items-center gap-4 p-8 rounded-2xl bg-zinc-800 border-2 border-zinc-700 hover:border-emerald-500 hover:bg-zinc-800/80 transition-all duration-200"
            >
              <div className="w-16 h-16 rounded-xl bg-zinc-700 group-hover:bg-emerald-500/20 flex items-center justify-center transition-colors">
                <Store className="w-8 h-8 text-zinc-300 group-hover:text-emerald-400 transition-colors" />
              </div>
              <span className="text-xl font-semibold text-white">{restaurant.name}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (!activeRestaurant) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-zinc-900 text-white">
        <p>Nenhum restaurante encontrado</p>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden bg-zinc-900 text-zinc-100">
      <Outlet />
    </div>
  );
}
